/* eslint-disable react/prop-types */
import { FormControl, FormHelperText, SxProps, TextField, Typography } from "@mui/material";
import { FC, useContext, useState } from "react";
import { Controller, ControllerRenderProps, FieldValues, useFormContext, useWatch } from "react-hook-form";
import HelperText from "./HelperText";
import { ComputeSaveProps, PPTextFieldProps } from "./PPField";
import { AppContextType, StateContext, StateContextType } from "../../context/ApplicationContext";
import { evaluateExpression } from "../../functions/parser";
import { ExtendedEvents, FunctionDefinition, NameValueTuple } from "../model/form-configuration";
import { funcExecutor } from "../../functions/generic-functions";
import { cloneDeep, isEqual } from "lodash";
import { red } from "@mui/material/colors";

const style: SxProps = {
  color: "gray",
};

type InnerTextFieldProps = {
    props: PPTextFieldProps;
    field: ControllerRenderProps<FieldValues, string>;
  };
  
  const InnerComputeSaveField: FC<InnerTextFieldProps> = ({
    props,
    field,
  }: InnerTextFieldProps) => {
    const fontColor: string = "text.secondary";
    const fieldIsRequired: string = "Field is required";
    const invalidFormat: string = "Invalid format";
    const { control, getValues, setValue } = useFormContext();
    const stateCtx: StateContextType = useContext(StateContext);
  
    const appContext = stateCtx.applicationContext;
    const setAppContext = stateCtx.setApplicationContext;
  
    const requiredTokens: string[] = props.extendedAttributes["required"]
      ? props.extendedAttributes["required"].split(" ")
      : [];
  
    const watch: FieldValues = useWatch({ control, defaultValue: undefined });
    const isRequired: boolean = evaluateExpression(requiredTokens, watch);
  
    const getParameter = (nameValue: NameValueTuple): any => {
      let param: any = undefined;
      switch (nameValue.type) {
        case "string":
          param = getString(nameValue);
          break;
        case "stringArray":
          param = getStringArray(nameValue);
          break;
        case "rpn":
          param = getExpression(nameValue);
          break;
        default:
          param = nameValue.value;
      }
      return param;
    };
  
    const getString = (nameValue: NameValueTuple): string => nameValue.value;
  
    const getExpression = (nameValue: NameValueTuple): string => nameValue.value;
  
    const getStringArray = (nameValue: NameValueTuple): string[] => {
      const stringArrRegEx: RegExp = /\[(.*?)\]/;
      const stringVals: RegExpMatchArray | null =
        nameValue.value.match(stringArrRegEx);
      if (stringVals !== null) {
        return stringVals[1].split(",");
      } else return [] as string[];
    };
  
    const onChange = (appContext: AppContextType) => {
      const events: ExtendedEvents | undefined = props.extendedEvents;
  
      if (events?.onChange) {
        const functions: FunctionDefinition[] = events.onChange;
        const sortedFunctions: FunctionDefinition[] = functions
          .slice()
          .sort(
            (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
          );
  
        sortedFunctions.forEach((func: FunctionDefinition) => {
          const sortedParams: NameValueTuple[] = func.parameters
            .slice()
            .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);
  
          funcExecutor(
            func.name,
            sortedParams.map((nameValue: NameValueTuple): any =>
              getParameter(nameValue)
            ),
            getValues,
            setValue,
            appContext
          );
        });
      }
    };
  
    const [errorMessage, setErrorMessage]: [
      string,
      React.Dispatch<React.SetStateAction<string>>
    ] = useState(() => fieldIsRequired);
  
    const isFieldInvalid: boolean =
      appContext.fieldState[field.name] !== undefined &&
      !appContext.fieldState[field.name];
    const isHideField: boolean =
      appContext.fieldHide[field.name] !== undefined &&
      appContext.fieldHide[field.name];
    const isDisabledField: boolean =
      appContext.fieldDisable[field.name] !== undefined &&
      appContext.fieldDisable[field.name];
  
    return (
      <FormControl error={isFieldInvalid} fullWidth>
        <TextField
          {...field}
          sx={isHideField ? { display: "none" } : null}
          id={props.id}
          disabled={isDisabledField}
          value={field.value ? field.value : ''}
          onChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            field.onChange(event.target.value);
  
            const formatRegEx: RegExp =
              props.extendedAttributes ?? props.extendedAttributes["format"]
                ? new RegExp(props.extendedAttributes["format"])
                : /.+/;
            let newAppContext: AppContextType = cloneDeep(appContext);
            const isValidFormat: boolean =
              event.target.value.length == 0 ||
              formatRegEx.test(event.target.value);
              
            newAppContext.fieldState[field.name] =
              (isRequired && event.target.value.length > 0 && isValidFormat) ||
              (!isRequired && (isValidFormat || event.target.value.length == 0));
            setErrorMessage(isValidFormat ? fieldIsRequired : invalidFormat);
  
            onChange(newAppContext);
            if (!isEqual(appContext, newAppContext)) setAppContext(newAppContext);
          }}
          label={
            <Typography sx={{ color: isFieldInvalid ? red.A700 : fontColor }}>
              {props.label + (isRequired ? " *" : "")}
            </Typography>
          }
          multiline
          inputProps={{
            maxLength: props.length,
            readOnly: true,
          }}
          variant="outlined"
          fullWidth
        />
        <FormHelperText sx={isHideField ? { display: "none" } : null}>
          {isFieldInvalid ? (
            errorMessage
          ) : (
            <HelperText helperText={props.helperText}></HelperText>
          )}
        </FormHelperText>
      </FormControl>
    );
  };

const ComputeSave: FC<ComputeSaveProps> = (props) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={props.id}
      control={control}
      defaultValue={props.value}
      render={({ field }) => {
      return (
        <InnerComputeSaveField props={props} field={field}/>
      )}}
    />
  );
};

export default ComputeSave;
//ComputeSave.whyDidYouRender = true;
